// One shared, self-creating image viewer for any "view this photo" link in the
// app (currently: purchase order receipts). Mirrors tooltip.js's pattern - a
// single element lazily appended to <body> on first use, self-initializing.
// Reuses .modal-backdrop so Javascript/modalBackdrop.js's global click-outside
// -to-close and its blur/dim styling apply for free; .pb-lightbox-close is
// registered there as a recognized close control.
//
// Zoom: buttons, mouse wheel, and touch pinch (via the unified Pointer Events
// API, so mouse-drag-to-pan and touch-pinch-to-zoom share one code path).
// Fullscreen uses the standard Fullscreen API, feature-detected - the button
// hides itself on browsers/platforms that don't support it (e.g. iOS Safari)
// rather than throwing.

const MIN_SCALE = 1;
const MAX_SCALE = 5;

let backdrop = null;
let stage = null;
let img = null;
let zoomLabel = null;
let fullscreenBtn = null;

let scale = 1;
let translateX = 0;
let translateY = 0;

const pointers = new Map();
let panStart = null;
let pinchStartDist = 0;
let pinchStartScale = 1;
let tapCandidate = null; // tracks a plain tap/click on the empty stage (not the image) to close
const TAP_THRESHOLD = 6;

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const clampTranslate = () => {
  const maxX = Math.max(0, (img.offsetWidth * scale - stage.clientWidth) / 2);
  const maxY = Math.max(0, (img.offsetHeight * scale - stage.clientHeight) / 2);
  translateX = clamp(translateX, -maxX, maxX);
  translateY = clamp(translateY, -maxY, maxY);
};

const applyTransform = () => {
  clampTranslate();
  img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
  zoomLabel.textContent = `${Math.round(scale * 100)}%`;
  stage.classList.toggle("pb-lightbox-stage--zoomed", scale > 1);
};

const setScale = (next) => {
  scale = clamp(next, MIN_SCALE, MAX_SCALE);
  if (scale === MIN_SCALE) { translateX = 0; translateY = 0; }
  applyTransform();
};

const resetZoom = () => { scale = 1; translateX = 0; translateY = 0; applyTransform(); };

const ensureEl = () => {
  if (backdrop) return;

  backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop pb-lightbox-backdrop hidden";
  backdrop.innerHTML = `
    <div class="pb-lightbox-toolbar">
      <span class="pb-lightbox-zoom-label">100%</span>
      <button class="pb-lightbox-tool" data-act="zoom-out" type="button" aria-label="Zoom out">&minus;</button>
      <button class="pb-lightbox-tool" data-act="zoom-in" type="button" aria-label="Zoom in">+</button>
      <button class="pb-lightbox-tool" data-act="reset" type="button" aria-label="Reset zoom">Reset</button>
      <button class="pb-lightbox-tool" data-act="fullscreen" type="button" aria-label="Toggle fullscreen">&#x26F6;</button>
      <button class="pb-lightbox-close" type="button" aria-label="Close">&times;</button>
    </div>
    <div class="pb-lightbox-stage">
      <img class="pb-lightbox-img" alt="" draggable="false" />
      <p class="pb-lightbox-error hidden">Couldn't load this image.</p>
    </div>
  `;
  document.body.appendChild(backdrop);

  stage = backdrop.querySelector(".pb-lightbox-stage");
  img = backdrop.querySelector(".pb-lightbox-img");
  zoomLabel = backdrop.querySelector(".pb-lightbox-zoom-label");
  fullscreenBtn = backdrop.querySelector('[data-act="fullscreen"]');
  const errorMsg = backdrop.querySelector(".pb-lightbox-error");

  if (!document.fullscreenEnabled) fullscreenBtn.classList.add("hidden");

  img.addEventListener("load", () => { errorMsg.classList.add("hidden"); img.classList.remove("hidden"); });
  img.addEventListener("error", () => { errorMsg.classList.remove("hidden"); img.classList.add("hidden"); });

  backdrop.querySelector(".pb-lightbox-close").addEventListener("click", close);
  backdrop.querySelector('[data-act="zoom-in"]').addEventListener("click", () => setScale(scale * 1.4));
  backdrop.querySelector('[data-act="zoom-out"]').addEventListener("click", () => setScale(scale / 1.4));
  backdrop.querySelector('[data-act="reset"]').addEventListener("click", resetZoom);
  fullscreenBtn.addEventListener("click", () => {
    if (document.fullscreenElement === backdrop) document.exitFullscreen?.();
    else backdrop.requestFullscreen?.().catch(() => {});
  });
  document.addEventListener("fullscreenchange", () => {
    fullscreenBtn?.classList.toggle("pb-lightbox-tool--active", document.fullscreenElement === backdrop);
  });

  img.addEventListener("dblclick", () => (scale > 1 ? resetZoom() : setScale(2.5)));

  stage.addEventListener("wheel", (e) => {
    if (backdrop.classList.contains("hidden")) return;
    e.preventDefault();
    setScale(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
  }, { passive: false });

  stage.addEventListener("pointerdown", (e) => {
    stage.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchStartDist = dist(a, b);
      pinchStartScale = scale;
      panStart = null;
      tapCandidate = null;
    } else if (pointers.size === 1) {
      panStart = { x: e.clientX, y: e.clientY, tx: translateX, ty: translateY };
      // A tap that lands on the bare stage (not the image) and never turns
      // into a drag closes the viewer - the stage fills the whole backdrop,
      // so modalBackdrop.js's generic "click the backdrop itself" check can
      // never see a direct hit here; this is the equivalent for this viewer.
      tapCandidate = { target: e.target, x: e.clientX, y: e.clientY };
    }
  });

  stage.addEventListener("pointermove", (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      if (pinchStartDist > 0) setScale(pinchStartScale * (dist(a, b) / pinchStartDist));
    } else if (pointers.size === 1 && panStart && scale > 1) {
      translateX = panStart.tx + (e.clientX - panStart.x);
      translateY = panStart.ty + (e.clientY - panStart.y);
      applyTransform();
    }
    if (tapCandidate && dist(tapCandidate, { x: e.clientX, y: e.clientY }) > TAP_THRESHOLD) {
      tapCandidate = null;
    }
  });

  const endPointer = (e) => {
    pointers.delete(e.pointerId);
    pinchStartDist = 0;
    if (pointers.size === 0 && tapCandidate?.target === stage) close();
    tapCandidate = null;
    panStart = pointers.size === 1
      ? { x: [...pointers.values()][0].x, y: [...pointers.values()][0].y, tx: translateX, ty: translateY }
      : null;
  };
  stage.addEventListener("pointerup", endPointer);
  stage.addEventListener("pointercancel", endPointer);

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || backdrop.classList.contains("hidden")) return;
    if (document.fullscreenElement === backdrop) return; // let the browser exit fullscreen first
    close();
  });
};

const close = () => {
  if (!backdrop) return;
  backdrop.classList.add("hidden");
  if (document.fullscreenElement === backdrop) document.exitFullscreen?.();
  pointers.clear();
  panStart = null;
};

// url is a Supabase Storage public URL from our own upload pipeline (never
// raw user text). Assigned as DOM properties (.src/.alt), not innerHTML, so
// no esc() - same as every other image src assignment in the app.
export const openLightbox = function (url, altText = "") {
  if (!url) return;
  ensureEl();
  resetZoom();
  img.src = url;
  img.alt = altText;
  backdrop.classList.remove("hidden");
};
